// Per-intent accordion. One entry per `intents[]` element from v3.

import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Accordion, AccordionSummary, AccordionDetails,
  Box, Stack, Typography, Chip,
} from "@mui/material";
import { ChevronDown } from "lucide-react";
import { formatCweMitreOwasp } from "../../../../../utils/wafEventV3";
import colors from "../../../../../utils/colors";
import {
  BORDER_COLOR,
  TEXT_PRIMARY,
  TEXT_SECONDARY,
  drawerSeverityPalette,
} from "../wafEventDrawerShared";
import {
  spBodySx,
  spSecondaryBodySx,
  spSectionLabelSx,
  spMonoFontFamily,
  spNumberedRowSx,
  spClassificationChipSx,
  spSeverityChipSx,
  SP,
  spLh,
} from "./smartParseDrawerUi";

function ClassificationChips({ classification }) {
  const items = formatCweMitreOwasp(classification);
  if (items.length === 0) return null;
  return (
    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
      {items.map((label) => (
        <Chip
          key={label}
          size="small"
          label={label}
          sx={spClassificationChipSx}
        />
      ))}
    </Stack>
  );
}

function SeverityChip({ severity }) {
  const palette = drawerSeverityPalette(severity);
  if (!severity || palette.label === "—") return null;

  return (
    <Chip
      size="small"
      label={palette.label}
      sx={spSeverityChipSx(severity)}
    />
  );
}

function EvidenceCards({ evidence }) {
  if (!Array.isArray(evidence) || evidence.length === 0) {
    return (
      <Typography sx={{ ...spSecondaryBodySx, fontStyle: "italic" }}>
        No evidence rows surfaced.
      </Typography>
    );
  }

  return (
    <Stack spacing={1.5}>
      {evidence.map((e, i) => (
        <Box key={i} sx={spNumberedRowSx}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 1 }}>
          <Typography sx={{ fontSize: 12.5, fontWeight: 600, lineHeight: spLh.normal, color: TEXT_PRIMARY }}>
              {e.signal || "—"}
            </Typography>
            <SeverityChip severity={e.severity} />
          </Stack>
          <Stack spacing={0.75}>
            {e.scope && (
              <Stack direction="row" spacing={1}>
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: TEXT_SECONDARY, minWidth: 72, lineHeight: spLh.px18 }}>Scope</Typography>
                <Typography sx={{ fontSize: 13, color: TEXT_PRIMARY, lineHeight: spLh.px21 }}>{e.scope}</Typography>
              </Stack>
            )}
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: TEXT_SECONDARY, minWidth: 72, flexShrink: 0, lineHeight: spLh.px18 }}>Path</Typography>
              <Typography
                sx={{
                  fontSize: 12,
                  lineHeight: spLh.px18,
                  color: e.matched_value ? TEXT_PRIMARY : TEXT_SECONDARY,
                  fontFamily: e.matched_value ? spMonoFontFamily : "inherit",
                  fontStyle: e.matched_value ? "normal" : "italic",
                  wordBreak: "break-all",
                }}
              >
                {e.matched_value || "not available"}
              </Typography>
            </Stack>
            {e.explanation && (
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: TEXT_SECONDARY, minWidth: 72, flexShrink: 0, lineHeight: spLh.px18 }}>Behavioral</Typography>
                <Typography sx={{ fontSize: 13, lineHeight: spLh.px21, color: TEXT_PRIMARY, flex: 1 }}>
                  {e.explanation}
                </Typography>
              </Stack>
            )}
          </Stack>
        </Box>
      ))}
    </Stack>
  );
}

function IntentSection({ intent, expanded, onToggle }) {
  return (
    <Accordion
      expanded={expanded}
      onChange={onToggle}
      disableGutters
      elevation={0}
      sx={{
        border: `1px solid ${BORDER_COLOR}`,
        borderRadius: "12px !important",
        overflow: "hidden",
        "&:before": { display: "none" },
        mb: 1.5,
        "&:last-child": { mb: 0 },
      }}
    >
      <AccordionSummary
        expandIcon={<ChevronDown size={16} color={TEXT_PRIMARY} />}
        sx={{
          px: "13px",
          py: "12px",
          minHeight: 44,
          backgroundColor: colors.neutral[50],
          "& .MuiAccordionSummary-content": { my: 0.5, mr: 1 },
          "&:hover": { backgroundColor: colors.neutral[200] },
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ width: "100%", minWidth: 0 }}>
          <Typography sx={{ fontSize: 13.5, fontWeight: 600, lineHeight: spLh.snug, color: TEXT_PRIMARY }}>
            {intent.intent_name || intent.intent_id || "Unknown intent"}
          </Typography>
          <SeverityChip severity={intent.severity} />
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ px: "13px", pt: 0, pb: "13px" }}>
        <Stack sx={{ gap: "12px" }}>
          <ClassificationChips classification={intent.classification} />
          {intent.objective && (
            <Typography sx={spBodySx}>{intent.objective}</Typography>
          )}
          <Box>
            <Typography sx={spSectionLabelSx}>Evidence</Typography>
            <EvidenceCards evidence={intent.evidence} />
          </Box>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}

export default function IntentsPanel({ intents }) {
  const [expandedIdx, setExpandedIdx] = useState(0);

  if (!Array.isArray(intents) || intents.length === 0) {
    return null;
  }

  return (
    <Box>
      <Typography sx={{ ...spSecondaryBodySx, mb: 2 }}>
        SmartParse matched {intents.length} corroborating intent{intents.length !== 1 ? "s" : ""} from path and behavioural analysis.
      </Typography>
      {intents.map((intent, i) => (
        <IntentSection
          key={intent.intent_id || `intent-${i}`}
          intent={intent}
          expanded={expandedIdx === i}
          onToggle={() => setExpandedIdx(expandedIdx === i ? -1 : i)}
        />
      ))}
    </Box>
  );
}

IntentsPanel.propTypes = {
  intents: PropTypes.array,
  primaryIntent: PropTypes.object,
};
